import { JSX, useState } from 'react';
import {
  TextField,
  InputAdornment,
  IconButton,
  Box,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SecurityIcon from '@mui/icons-material/Security';

interface Props {
  value: string;
  onChange: (v: string) => void;
}

/**
 * Formats raw digits as XXX-XX-XXXX for storage/API (matches convention of all
 * other SSN inputs in the codebase and what answerHelpers.ts expects).
 */
function formatDashed(digits: string): string {
  if (digits.length < 4) {
    return digits;
  }
  if (digits.length < 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

/** Converts stored value to XXX-XX-XXXX (dashes) for the visible display state. */
function getFormattedDisplay(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length < 4) {
    return digits;
  }
  if (digits.length < 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
}

/** Computes the partial-reveal masked display: •••-••-XXXX */
function getMaskedDisplay(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 0) {
    return '';
  }
  if (digits.length >= 6) {
    return `•••-••-${digits.slice(5, 9)}`;
  }
  if (digits.length >= 4) {
    return `•••-${digits.slice(3, 5)}`;
  }
  return '•'.repeat(digits.length);
}

export default function SSNInput({ value, onChange }: Props): JSX.Element {
  const [visible, setVisible] = useState(false);
  const [focused, setFocused] = useState(false);

  // value prop is dashed "XXX-XX-XXXX" (or partial while typing)
  // When focused+hidden: type=password so the browser masks naturally — pass raw dashed value
  // When visible: always show space-formatted display
  // When blurred+hidden: show partial-reveal bullet mask
  const displayValue =
    focused && !visible
      ? value
      : visible
      ? getFormattedDisplay(value)
      : getMaskedDisplay(value);

  const inputType = focused && !visible ? 'password' : 'text';

  return (
    <Box>
      <TextField
        fullWidth
        type={inputType}
        autoComplete="one-time-code"
        value={displayValue}
        onChange={(e) => {
          const digits = e.target.value.replace(/\D/g, '').slice(0, 9);
          onChange(formatDashed(digits));
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        inputProps={{
          inputMode: 'numeric',
          'data-testid': 'ssn-input',
          'data-dd-privacy': 'mask',
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setVisible((v) => !v)}
                aria-label={visible ? 'Hide SSN' : 'Show SSN'}
                data-testid="ssn-toggle"
              >
                {visible ? <VisibilityIcon /> : <VisibilityOffIcon />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1 }}>
        <SecurityIcon sx={{ fontSize: 14, color: 'primary.main' }} />
        <Typography
          variant="caption"
          sx={{ color: 'primary.main', fontWeight: 500 }}
        >
          Encrypted and secure
        </Typography>
      </Box>
    </Box>
  );
}
